import {
  createApi,
  fetchBaseQuery,
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import { logOut } from "../store/slices/authSlice";
import axios from "axios";
import { API_CONFIG } from "../config/api.config";
import useAuthStore from '../store/authStore';
import { Middleware, MiddlewareAPI, Dispatch, Action } from '@reduxjs/toolkit';
import { RootState } from '../store/store';

// In development, use the proxy. In production, use the full URL
const baseUrl = 'https://s3.ts1cloud.com';
const authUrl = import.meta.env.VITE_AUTH_API_URL || 'https://auth.ts1cloud.com';

export const prepareCommonHeaders = () => {
  const user = useAuthStore.getState().user;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    'Origin': window.location.origin
  };

  if (user) {
    headers['x-company-id'] = user.companyID.toString();
    headers['x-company-name'] = user.companyName;
  }

  return headers;
};

export const baseQuery = fetchBaseQuery({
  baseUrl: API_CONFIG.baseURL,
  credentials: 'include',
  prepareHeaders: (headers) => {
    const commonHeaders = prepareCommonHeaders();
    Object.entries(commonHeaders).forEach(([key, value]) => {
      headers.set(key, value);
    });
    return headers;
  }
});

// Helper to check if response indicates session expired
export const isSessionExpired = (error: any) => {
  return error?.status === 401 || 
         error?.data?.message?.toLowerCase().includes('session expired') ||
         error?.data?.message?.toLowerCase().includes('invalid session');
};

// Helper to handle session expiration
export const handleSessionExpiration = () => {
  useAuthStore.getState().logout();
  window.location.href = '/login';
};

interface ErrorAction extends Action<string> {
  payload: {
    error: {
      status: number;
      data: unknown;
    };
  };
}

// Middleware to handle session expiration
export const sessionMiddleware: Middleware<
  {},
  RootState,
  Dispatch<Action<string>>
> = (api: MiddlewareAPI<Dispatch<Action<string>>, RootState>) =>
  (next) =>
  (action: Action<string>) => {
    // Type guard to check if action has the expected structure
    const hasError = (action: Action<string>): action is ErrorAction => {
      return 'payload' in action &&
             action.payload !== null &&
             typeof action.payload === 'object' &&
             'error' in action.payload;
    };

    if (hasError(action) && isSessionExpired(action.payload.error)) {
      handleSessionExpiration();
    }
    return next(action);
  };

export const baseQueryWithReauth: BaseQueryFn<
  FetchArgs,
  unknown,
  FetchBaseQueryError,
  {},
  {}
> = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);
  
  if (result.error && 'status' in result.error && result.error.status === 401) {
    // Try to refresh the session
    try {
      const state = api.getState() as { auth: { user: any } };
      const user = state.auth.user;
      
      if (user) {
        const response = await fetch('https://auth.ts1cloud.com/auth/verifyAccess', {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'x-company-id': user.companyID.toString(),
            'x-company-name': user.companyName,
            'Origin': window.location.origin
          }
        });

        if (response.ok) {
          // Retry the original request
          result = await baseQuery(args, api, extraOptions);
        } else {
          api.dispatch(logOut());
        }
      }
    } catch (error) {
      api.dispatch(logOut());
    }
  }
  
  return result;
};

export const rootApi = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Sensors', 'BaseUnits', 'LiveData', 'DayData', 'Zones', 'ZoneSensors', 'UserZone', 'Alarms'],
  endpoints: () => ({}),
});

export const authAxios = axios.create({
  baseURL: authUrl,
  withCredentials: true
});

// Helper to create a base query with a specific base URL
export const createBaseQuery = (basePath: string = '') =>
  fetchBaseQuery({
    baseUrl: basePath,
    credentials: "include",
    prepareHeaders: (headers, { getState }) => {
      // Get user from Redux store
      const state = getState();
      const user = (state as any).auth?.user;

      headers.set('Content-Type', 'application/json');
      headers.set('Accept', 'application/json');
      headers.set('X-Requested-With', 'XMLHttpRequest');
      headers.set('Origin', window.location.origin);

      if (user) {
        headers.set('x-company-id', user.companyID.toString());
        headers.set('x-company-name', user.companyName);
      }

      // Debug: log user and headers for every request
      const debugHeaders = {};
      headers.forEach((v, k) => { debugHeaders[k] = v; });
      console.log('[API DEBUG] user:', user, 'headers:', debugHeaders);

      return headers;
    },
  });